/* eslint-disable no-case-declarations */
import { isEqual } from 'lodash';

import { formatToPhoneNumber } from 'utils/phone-number';
import { DEFAULT_RING_PATTERN } from 'constants/phone-number';

export const actionTypes = {
  SET_AVAILABLE_NUMBERS: 'SET_AVAILABLE_NUMBERS',
  SET_CAN_SAVE: 'SET_CAN_SAVE',
  SET_INITIAL_DATA: 'SET_INITIAL_DATA',
  SET_IS_LOADING: 'SET_IS_LOADING',
  SET_REMOVE_MODAL: 'SET_REMOVE_MODAL',
  UPDATE_AVAILABLE_NUMBERS: 'UPDATE_AVAILABLE_NUMBERS',
  UPDATE_INITIAL_DATA: 'UPDATE_INITIAL_DATA',
  SET_EXTENSION_ERRORS: 'SET_EXTENSION_ERRORS',
  SET_CONFIRM_SMS: 'SET_CONFIRM_SMS',
  SET_DELETE_PRIMARY_SMS: 'SET_DELETE_PRIMARY',
};

const defaultPhoneShape = {
  extension: '',
  phoneNumber: '',
  unreachableForwardingNumber: '',
};

export const initialState = {
  assignedNumbers: [],
  availableNumbers: [],
  canSave: false,
  isLoading: true,
  locationId: null,
  origAlternatePhoneNumbers: [],
  origAvailableNumbers: [],
  origPrimaryPhoneNumber: defaultPhoneShape,
  removeModal: {
    index: null,
    isLoading: false,
    numberToRemove: '',
    open: false,
  },
  ringPatterns: [],
  extensionErrors: [],
  confirmSMS: false,
};

export const reducer = (state, action) => {
  const { type, payload } = action;

  switch (type) {
    case actionTypes.RESET_AVAILABLE_NUMBERS: {
      return { ...state, availableNumbers: state.origAvailableNumbers };
    }
    case actionTypes.SET_INITIAL_DATA:
      const { origAlternatePhoneNumbers, availableNumbers } = payload;

      const filteredNumbers = availableNumbers.filter(
        (num) =>
          !origAlternatePhoneNumbers.some(
            (phoneObj) => phoneObj.phoneNumber === num,
          ),
      );

      const sorted = filteredNumbers.sort((a, b) => a - b);

      const available = [];
      sorted.forEach((num) => {
        if (!num) return;

        available.push({
          label: formatToPhoneNumber(num),
          value: num,
        });
      });

      return {
        ...state,
        ...payload,
        isLoading: false,
        availableNumbers: available,
        origAvailableNumbers: available,
      };

    case actionTypes.SET_IS_LOADING:
      return {
        ...state,
        isLoading: payload,
      };

    case actionTypes.SET_CAN_SAVE: {
      const {
        alternatePhoneNumbers,
        primaryPhoneNumber = { ...defaultPhoneShape },
        removeOnly = false,
      } = payload;

      // we only check for an extension if we did NOT already have one saved to the team, otherwise it should be impossible for a user to set a new extension
      if (
        !state.origPrimaryPhoneNumber.extension &&
        primaryPhoneNumber.extension
      ) {
        return { ...state, canSave: true };
      }

      if (
        state.origPrimaryPhoneNumber.phoneNumber !==
        primaryPhoneNumber.phoneNumber
      ) {
        return { ...state, canSave: true };
      }

      if (
        state.origPrimaryPhoneNumber.unreachableForwardingNumber !==
        primaryPhoneNumber.unreachableForwardingNumber
      ) {
        return { ...state, canSave: true };
      }

      for (let i = 0; i < alternatePhoneNumbers.length; i += 1) {
        if (
          state.origAlternatePhoneNumbers[i] &&
          isEqual(
            state.origAlternatePhoneNumbers[i],
            alternatePhoneNumbers[i],
          ) === false
        ) {
          return { ...state, canSave: true };
        }

        const newAlternateIsModified =
          alternatePhoneNumbers[i].phoneNumber ||
          alternatePhoneNumbers[i].extension ||
          alternatePhoneNumbers[i].ringPattern !== DEFAULT_RING_PATTERN;

        if (!state.origAlternatePhoneNumbers[i] && newAlternateIsModified) {
          return { ...state, canSave: removeOnly === false };
        }
      }

      return { ...state, canSave: false };
    }

    case actionTypes.SET_REMOVE_MODAL:
      return { ...state, removeModal: { ...state.removeModal, ...payload } };

    case actionTypes.UPDATE_AVAILABLE_NUMBERS: {
      const { prevVal, newVal } = payload;

      const newAvailableNums = [...state.availableNumbers];

      const index = newAvailableNums.findIndex(
        (element) => element.value === newVal,
      );
      if (index !== -1) newAvailableNums.splice(index, 1);

      if (prevVal !== '' && prevVal !== newVal) {
        newAvailableNums.push({
          label: formatToPhoneNumber(prevVal),
          value: prevVal,
        });
      }

      newAvailableNums.sort((a, b) => a.value - b.value);

      return { ...state, availableNumbers: newAvailableNums };
    }

    case actionTypes.UPDATE_INITIAL_DATA:
      return { ...state, ...payload };

    case actionTypes.SET_EXTENSION_ERRORS:
      return { ...state, extensionErrors: payload };

    case actionTypes.SET_CONFIRM_SMS:
      return { ...state, confirmSMS: payload };

    case actionTypes.SET_DELETE_PRIMARY_SMS:
      return { ...state, deletePrimary: payload };

    default:
      return state;
  }
};
